/* eslint-disable angular/no-private-call */

import { paginationHelperModule } from '../pagination-helper/pagination-helper';
import { addUnique } from '../../utils/array-utils';

export const autolistHelperModule = angular
  .module('Q.helpers.autolist', [paginationHelperModule])

  /**
   * This service is used to create lists that will automatically update when items
   * are added or removed during push state events.
   *
   * It also can be used to return a list that is filled asynchronously, or a list that needs to be
   * manually re-updated.
   *
   * The list can also track a filter or sort, and automatically reapply the filter and sort to the
   * source list when updating.
   *
   * Autolists also provide a consistent promise interface even to synchronous lookup functions, via
   * list.promise.
   *
   */

  .factory('autolist', function ($rootScope, $q, $timeout, paginationHelper) {
    const uniqueLists = {};

    const SOCKET_DEBOUNCE = 'socketDebounceTimeout';
    const WATCHER_DEBOUNCE = 'watcherDebounceTimeout';

    return {
      create,
    };

    //// Public Functions

    /**
     * Creates a new autolist based on the provided lookup function
     *
     * @param lookupFunction {Function} function that generates the initial list
     * @param Model {Object?} Model that this list will contain (helps narrow down event listeners)
     * @param options {Object?}
     * @param options.autolistId {String?} If provided, will only update if this list is the current version of the unique ID
     * @param options.modifyResult {Function<result,autolist>?} function called after list is processed
     * @returns {Array}
     */
    function create(
      lookupFunction,
      Model,
      { autolistId, modifyResult: _modifyResult } = {},
    ) {
      const list = [];

      angular.forEach(
        {
          $$cache: {
            lookupFunction,
            Model,
            autolistId,
          },
          canUpdate,
          update,
          autoSort,
          autoFilter,
          paginate,
          setPageFor,
          modifyResult,
          processUpdate,
          listen,
          stopListening,
          destroy,
          originalList: [],
          filteredList: null,
          sortedList: null,
          pg: {},
          hasLoaded: false,
          isEventUpdate: false,
          isWatchUpdate: false,
        },
        (value, prop) => {
          Object.defineProperty(list, prop, { value, writable: true });
        },
      );

      if (autolistId) {
        uniqueLists[autolistId] = list;
      }

      if (_modifyResult) {
        list.modifyResult(_modifyResult);
      }

      list.update();

      return list;
    }

    //// Public Functions on an autolist

    /**
     * Returns false if this model should not be updated
     * @returns {boolean}
     */
    function canUpdate() {
      const list = this;
      return (
        !list.$destroyed &&
        (!list.$$cache.autolistId ||
          uniqueLists[list.$$cache.autolistId] === list)
      );
    }

    /**
     * Updates the list by reprocessing the original lookupFunction,
     * and reapplying any filters or sorts on the result.
     *
     * A promise for the async (or even synchronous) lookup can be accessed via
     * list.promise
     *
     * @param options {object} Options for processing the list this time only
     * @param options.sticky {bool} Override the sticky state for this update
     * @param options.changes {object} Change info for this update
     * @param options.event {object} Event info for this update
     * @returns {Array} self
     */
    function update(options = {}) {
      const list = this;

      if (!list.canUpdate()) return list;
      list.updatingError = false;

      function markLoaded() {
        list.hasLoaded = true;
        list.updatingError = false;
        list.dirty = false;
        $timeout(() => (list.finishedLoading = true), 1);
      }

      if (
        options.changes &&
        Object.keys(options.changes).length &&
        !options.changes.all &&
        list.originalList.length
      ) {
        // don't reload if we're only handling local sort or pagination changes
        markLoaded();
        _processResult(list, list.originalList, options);
        list.promise = $q.when(list);
      } else {
        let result = list.$$cache.lookupFunction(options);
        if (result.then) {
          list.updating = true;
          list.promise = result.then(
            function (data) {
              list.updating = false;
              markLoaded();
              return _processResult(list, data, options);
            },
            (err) => {
              markLoaded();
              list.updatingError = err || true;
              return $q.reject(err);
            },
          );
        } else {
          markLoaded();
          _processResult(list, result, options);
          list.promise = $q.when(list);
        }
      }

      return list;
    }

    /**
     * Creates an auto sorting function on this list.  This sort will be reapplied
     * if the list contents get updated.
     *
     * @param fn {function|bool} sort function, true for default sort, or false to disable the sort
     * @returns {Array} self
     */
    function autoSort(fn) {
      const list = this;
      list.$$cache.sort = fn;
      _processResult(list, undefined, { changes: { sort: true } });
      return list;
    }

    /**
     * Creates an auto filtering function on this list.  This filter will be reapplied
     * if the list contents get updated.
     *
     * @param fn {function} filter function
     * @param sticky {bool} If provided, don't remove items from the list when updating
     * @returns {Array} self
     */
    function autoFilter(fn, { sticky } = {}) {
      const list = this;
      list.$$cache.filter = fn;
      _processResult(list);
      // we set sticky _after_ the first time the list is filtered, otherwise the list is
      // never filtered!
      list.$$cache.sticky = sticky;
      return list;
    }

    /**
     * Configures automatic pagination
     * @param opts {object|function} The options to pass into paginate
     * @returns {Array} self
     */
    function paginate(opts) {
      const list = this;
      list.$$cache.pagination = opts;
      _processResult(list, undefined, { changes: { pagination: true } });
      return list;
    }

    /**
     * Switches to the page for the provided item or id
     * @param itemOrId
     * @returns {number} page we switched to (for updating routes, etc)
     */
    function setPageFor(itemOrId) {
      const list = this;
      _processResult(list, undefined, {
        paginationCurrentItem: itemOrId,
        changes: { pagination: true },
      });
      return list.pg.page;
    }

    /**
     * If provided, allows modification of the final list after updates, filtering, sorting, and pagination
     * @param fn {function<Array,autolist>}
     */
    function modifyResult(fn) {
      const list = this;
      list.$$cache.modifyResult = fn;
      if (list.hasLoaded) {
        _processResult(list);
      }
      return this;
    }

    /**
     * Runs a function on the list during every update.  This function is called before the filter
     * or sort are applied.
     *
     * The function is provided with two arguments: the new data, and the autolist in it's current state.
     *
     * If the function returns an array, that array is assumed to replace the new data.
     * If the function returns false, the update is halted.
     * All other results from the function are discarded.
     *
     * This function could be used to load missing relationships, or perform per-item modifications.
     *
     * @param fn Function to run after every update
     * @returns {Array} self
     */
    function processUpdate(fn) {
      this.$$cache.processUpdate = fn;
      return _processResult(this);
    }

    /**
     * Creates a listener on the provided scope (recommended) or $rootScope if one is not provided.
     * This listener will listen to create and destroy events for the provided model, as well as the optional
     * watcher function.
     *
     * Watcher events are assumed to be important, and are updated immediately. They are debounced to .5s by
     * default, ensuring relatively quick updates while ignoring multiple sequential event triggers.
     *
     * Socket and model events are delayed and debounced to 15s by default. This helps cut down on refresh noise.
     * Socket events can also be disabled completely using `updated: false`, which will only mark the list as
     * `dirty`, and not cause an update. In this case, calling `list.update()` will reload the list immediately
     * and clear the dirty flag.
     *
     * If no local scope is provided, it is up to the endpoint to call stopListening or the returned
     * deregistration function on this list, or there *will* be a memory leak and performance issues.
     *
     * @param {$rootScope.Scope} scope Local scope to attach to
     * @param {boolean?} update If false, don't update the list, just mark it as dirty
     * @param {number?} socketDebounce Adjust the debounce rate for socket updates. Defaults to 15,000ms (15 seconds).
     * @param {number?} socketInitialDelay Always delay socket updates by this amount. Defaults to 100ms.
     * @param {number?} watcherDebounce Adjust the debounce rate for watcher updates. Defaults to 500ms.
     * @param {number?} watcherInitialDelay Always delay watched updates by this amount. Defaults to 0ms, which still groups multiple events per digest.
     * @param {Function?} watcher Additional watcher function (updates on change except the first)
     * @param {boolean?} deepWatch If using the additional watcher, is this a deep watch?  Defaults to false.
     * @param {string?} sortKey If provided, helps determine if we only need to sort (and paginate) the results without reloading (during watch)
     * @param {string?} pageKey If provided, helps determine if we only need to paginate the results without reloading (during watch)
     * @param {Function|boolean?} sticky If provided, only update items within the list (using Model.find()) when we can.
     * @returns {Function} deregistration function
     */
    function listen({
      scope = $rootScope,
      update = true,
      socketDebounce = 15000,
      socketInitialDelay = 100,
      watcherDebounce = 500, // don't update faster than every 15s in general
      watcherInitialDelay = 0,
      watcher = null,
      deepWatch = false,
      sortKey = null,
      pageKey = null,
      sticky = null,
    } = {}) {
      const list = this;
      list.stopListening();

      const $$cache = list.$$cache;
      $$cache.listeners = [];

      let changes = {};
      let event = {};
      let ids = [];

      function _handleUpdate() {
        // reset any updates that came in before the update started.
        delete $$cache.hasDebouncedUpdates;

        // if we're in sticky mode, and every id we've caught is in our visible list,
        // then don't reload the list
        if (
          sticky &&
          ids.length &&
          ids.every((id) => list.some((item) => item.id === id))
        ) {
          list.forEach((item, index) => {
            if (ids.includes(item.id)) {
              $$cache.Model.find(item.id, { bypassCache: true }).then(
                (newItem) => {
                  list[index] = newItem;
                },
              );
            }
          });

          // prevent reloading the whole list if we can simply refresh individual items
          changes.all = false;
        }
        list.update({ changes, event });
        ids = [];
        changes = {};
        event = {};
      }

      // sets up a debounced update, which will automatically update after the timeout if there's a change
      // while waiting. Never updates faster than `debounce` milliseconds
      function _startDebounce(key, debounce) {
        $$cache[key] = $timeout(() => {
          delete $$cache[key];
          if ($$cache.hasDebouncedUpdates) {
            delete $$cache.hasDebouncedUpdates;
            _debouncedUpdate(key, 0, debounce);
          }
        }, debounce);
      }

      // If there's a delay in progress, marks that we have more changes and returns
      // otherwise starts an update immediately, then sets up a delay
      function _debouncedUpdate(key, initialDelay, debounce) {
        if ($$cache[key]) {
          $$cache.hasDebouncedUpdates = true;
          return;
        }
        list.dirty = true;

        // update after initialDelay to capture multiple rapid events
        $timeout(_handleUpdate, initialDelay);

        _startDebounce(key, debounce);
      }

      // listens to changes on sockets and model events, triggering debounced updates whenever one happens
      function modelChangedListener($event, data, type) {
        list.isEventUpdate = true;
        changes.all = true;
        event.data = data;
        event.type = type;
        if (data && data.id) {
          addUnique(ids, data.id);
        }
        list.dirty = true;
        if (update) {
          _debouncedUpdate(SOCKET_DEBOUNCE, socketInitialDelay, socketDebounce);
        }
      }

      if (update) {
        // Prevent updating immediately if we're performing updates.
        // This way the first update cannot happen until after the first socketDebounce.
        _startDebounce(SOCKET_DEBOUNCE, socketDebounce);
      }

      // configure all socket and model events
      let events = ['socket:model.changed', 'model.changed'];
      if ($$cache.Model) {
        events = events.map((n) => `${n}:${$$cache.Model.name}`);
      }
      events.forEach((event) => {
        $$cache.listeners.push(scope.$on(event, modelChangedListener));
      });

      // configure watcher listener
      if (watcher) {
        const watchListener = scope.$watch(
          watcher,
          (newVal, oldVal) => {
            if (newVal && !angular.equals(newVal, oldVal)) {
              if (list.$$cache.clearTypeFlagTimer) {
                $timeout.cancel(list.$$cache.clearTypeFlagTimer);
              }
              list.isWatchUpdate = true;
              Object.keys(newVal).forEach((k) => {
                if (newVal[k] !== oldVal[k]) {
                  if (k.match(pageKey)) {
                    changes.pagination = true;
                  } else if (k.match(sortKey)) {
                    changes.sort = true;
                  } else {
                    changes.all = true;
                  }
                }
              });

              _debouncedUpdate(
                WATCHER_DEBOUNCE,
                watcherInitialDelay,
                watcherDebounce,
              );
            }
          },
          deepWatch,
        );
        $$cache.listeners.push(watchListener);
      }

      return list.stopListening.bind(list);
    }

    /**
     * Deregisters the listener on this list.
     */
    function stopListening() {
      const $$cache = this.$$cache;
      if ($$cache.listeners) {
        $$cache.listeners.forEach((l) => l());
        $$cache.listeners = null;
      }
      [WATCHER_DEBOUNCE, SOCKET_DEBOUNCE].forEach((k) => {
        if ($$cache[k]) {
          $timeout.cancel($$cache[k]);
          delete $$cache[k];
        }
      });
    }

    /**
     * Destroys the auto list, stops listening, prevents further handling, etc.
     */
    function destroy() {
      const list = this;
      list.$destroyed = true;
      list.stopListening();
      list.length = 0;
      if (
        list.$$cache.autolistId &&
        uniqueLists[list.$$cache.autolistId] === list
      ) {
        delete uniqueLists[list.$$cache.autolistId];
      }
      Object.keys(list.$$cache).forEach((k) => {
        delete list.$$cache[k];
      });
      ['originalList', 'filteredList', 'sortedList', 'pg'].forEach((k) => {
        list[k] = undefined;
      });
      [
        'update',
        'autoSort',
        'autoFilter',
        'paginate',
        'setPageFor',
        'processUpdate',
        'listen',
        'stopListening',
        'destroy',
      ].forEach((k) => {
        list[k] = angular.noop;
      });
    }

    //// Private Functions

    /**
     * Handles processing a result, by running the auto filter and auto sort over it
     *
     * @param list autolist
     * @param result optional new list data
     * @param options {Object} pass in extra options to override the autolist's settings
     * @returns {Array} original autolist, now updated
     * @private
     */
    function _processResult(list, result = list.originalList, options = {}) {
      if (!list.canUpdate()) return;

      const $$cache = angular.extend({}, list.$$cache, options);

      let changes = options.changes;
      if (!changes || !(changes.sort || changes.pagination))
        changes = { all: true };
      changes.newResult = result !== list.originalList;

      if (changes.all || !result.length || !list.filteredList) {
        result = _processAndFilter(list, result, $$cache);
      } else {
        result = list.filteredList;
      }

      if (changes.all || changes.sort || !list.sortedList) {
        // sort the list (only happens if there is a cached sort function)
        result = _processSort(list, result, $$cache);
      } else {
        result = list.sortedList;
      }

      // pagination is last, we always process it
      result = _processPagination(list, result, $$cache);

      if (changes.newResult && $$cache.modifyResult) {
        result = $$cache.modifyResult(result, list, changes) || result;
      }

      // overwrite the original array contents with the new contents
      list.length = 0;
      list.push(...result);

      if (list.isEventUpdate || list.isWatchUpdate) {
        if (list.$$cache.clearTypeFlagTimer) {
          $timeout.cancel(list.$$cache.clearTypeFlagTimer);
        }
        list.$$cache.clearTypeFlagTimer = $timeout(() => {
          if (!$$cache.hasDebouncedUpdates) {
            list.isEventUpdate = false;
            list.isWatchUpdate = false;
          }
          delete list.$$cache.clearTypeFlagTimer;
        }, 2500);
      }

      if (angular.isFunction(list.onUpdate)) {
        list.onUpdate(list);
      }

      return list;
    }

    /**
     * Handles post-load processing and filtering of the results
     * @param list
     * @param result
     * @param $$cache
     * @returns {*}
     * @private
     */
    function _processAndFilter(list, result, $$cache) {
      // enable post-update processing
      if ($$cache.processUpdate) {
        const processUpdateResult = $$cache.processUpdate(result, list);
        // bail on false
        if (processUpdateResult === false) return list;

        // store original result no matter what
        list.originalList = result;

        // work on returned data
        if (Array.isArray(processUpdateResult)) {
          result = processUpdateResult;
        }
      } else {
        list.originalList = result;
      }

      // enable filtering
      if ($$cache.filter) {
        result = result.filter((item) => {
          let match = $$cache.filter(item);
          if (!match && $$cache.sticky && !list.isWatchUpdate) {
            match =
              list.filter((i) => i === item || (item.id && i.id === item.id))
                .length > 0;
          }
          return match;
        });
        list.filteredList = result;
      }

      return result;
    }

    /**
     * Handles post-filter sorting of the results
     * @param list
     * @param result
     * @param $$cache
     * @returns {*}
     * @private
     */
    function _processSort(list, result, $$cache) {
      let fn = $$cache.sort;
      if (fn) {
        if (fn === true) {
          result.sort();
        } else {
          if (fn.length !== 2) {
            fn = fn();
          }
          result.sort(fn);
        }
      }
      list.sortedList = result;

      return result;
    }

    /**
     * Handles post-sort pagination of the results
     * @param list
     * @param result
     * @param $$cache
     * @returns {*}
     * @private
     */
    function _processPagination(list, result, $$cache) {
      if ($$cache.pagination) {
        let opts = angular.isFunction($$cache.pagination)
          ? $$cache.pagination()
          : $$cache.pagination;
        if (opts.client === false) {
          // use the server details if this is a server query, except we don't support the buffer option
          list.pg = paginationHelper.calc(
            angular.extend({}, opts, { buffer: 0 }),
          );
        } else {
          let currentIndex = -1;
          if (opts.currentItem || $$cache.paginationCurrentItem) {
            let currentItem = opts.currentItem || $$cache.paginationCurrentItem;
            currentIndex = result.findIndex(
              (item) => item === currentItem || item.id === currentItem,
            );
          }
          list.pg = paginationHelper.calc(
            angular.extend({ total: result.length, currentIndex }, opts),
          );
          result = result.slice(list.pg.first, list.pg.last + 1);
        }
      } else {
        list.pg = {};
      }
      return result;
    }
  });
